import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ShieldCheck, Mail, Phone, Send, Loader2, Check, RotateCcw, X,
} from "lucide-react";
import WhatsappIcon from "./ui/WhatsappIcon";
import { PUBLIC_API } from "../lib/api";

/**
 * Unified identity verification widget.
 *
 * Used by all public flows (donate / campaign / kiddoush / festival).
 * Email et SMS → code à 6 chiffres saisi sur place (le serveur répond
 * ``mode: "code"``). WhatsApp → lien (polling). Reports the verified token
 * back via ``onVerified(token, channel)``.
 *
 * Pourquoi un code aussi par email (relecture du 01/10/2026) : un clic sur un
 * lien validait le navigateur qui avait lancé la demande, même celui d'un
 * tiers qui connaissait l'adresse. Le code se tape ICI, sur la page de la
 * demande : celui qui n'a rien demandé ne fait rien.
 *
 * Props:
 *  - organizationId (string, required)
 *  - email (string)     — current email from parent form
 *  - phone (string)     — current phone from parent form
 *  - firstName (string) — for nicer email greeting
 *  - onVerified(token, channel) — called when the donor confirms
 *  - disabled (bool)    — disables the trigger button (e.g. missing email)
 *  - context (string)   — "donate" | "campaign" | "kiddoush" (analytics only)
 *  - recognized (object|null) — donateur reconnu (recognize-donor) : ouvre les
 *    canaux ENREGISTRÉS chez la synagogue ({has_email, has_phone,
 *    masked_email, masked_phone}). Le contact réel n'est jamais affiché :
 *    l'envoi se résout côté serveur (use_donor_contact).
 */
export default function IdentityVerification({
  organizationId,
  email = "",
  phone = "",
  firstName = "",
  onVerified,
  disabled = false,
  context = "donate",
  recognized = null,
}) {
  const [smsEnabled, setSmsEnabled] = useState(false);
  const [whatsappEnabled, setWhatsappEnabled] = useState(false);
  const [orgConfigLoaded, setOrgConfigLoaded] = useState(false);
  const [mode, setMode] = useState("idle");
  // idle | choosing | sending | waiting | verified | expired
  const [token, setToken] = useState("");
  const [maskedDestination, setMaskedDestination] = useState("");
  const [channel, setChannel] = useState("email");
  const [codeMode, setCodeMode] = useState(false);
  const [code, setCode] = useState("");
  const [codeSubmitting, setCodeSubmitting] = useState(false);
  const [lockMsg, setLockMsg] = useState("");
  const [sendingKey, setSendingKey] = useState("");
  // Plafond SMS (miroir du serveur) : 1 min entre deux envois — chrono
  // affiché — et 3 SMS max par fenêtre de 30 min (horodatages purgés, la
  // carte SMS se ranime toute seule). L'email reste toujours disponible.
  const [smsSends, setSmsSends] = useState([]);
  const [cooldownUntil, setCooldownUntil] = useState(0);
  const [nowTs, setNowTs] = useState(Date.now());
  const pollRef = useRef(null);
  const codeSentRef = useRef(false);

  useEffect(() => {
    const needsTick = () =>
      cooldownUntil > Date.now()
      || (smsSends.length >= 3 && smsSends.some((t) => Date.now() - t < 1800000));
    if (!needsTick()) return undefined;
    const t = setInterval(() => {
      setNowTs(Date.now());
      if (!needsTick()) clearInterval(t);
    }, 1000);
    return () => clearInterval(t);
  }, [cooldownUntil, smsSends]);
  const cooldownLeft = Math.max(0, Math.ceil((cooldownUntil - nowTs) / 1000));
  const smsRecentCount = smsSends.filter((t) => nowTs - t < 1800000).length;
  const smsBlocked = smsRecentCount >= 3 || cooldownLeft > 0;

  // Fetch org config once to know if SMS verification is available
  useEffect(() => {
    if (!organizationId) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(
          `${PUBLIC_API}/public/identity/org-config/${organizationId}`,
        );
        if (!cancelled) {
          setSmsEnabled(Boolean(r.data?.sms_enabled));
          setWhatsappEnabled(Boolean(r.data?.whatsapp_enabled));
          setOrgConfigLoaded(true);
        }
      } catch {
        if (!cancelled) setOrgConfigLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [organizationId]);

  // Stop polling on unmount
  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  const startPolling = (currentToken) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const r = await axios.get(
          `${PUBLIC_API}/public/identity/${currentToken}/status`,
        );
        if (r.data?.expired) {
          clearInterval(pollRef.current);
          setMode("expired");
          toast.error("La vérification a expiré. Recommencez.");
          return;
        }
        if (r.data?.verified) {
          clearInterval(pollRef.current);
          setMode("verified");
          toast.success("Identité confirmée !");
          if (onVerified) onVerified(currentToken, r.data?.channel);
        }
      } catch {
        // ignore transient errors — keep polling
      }
    }, 3000);
  };

  // ── Options d'envoi : contacts TAPÉS d'abord, contacts ENREGISTRÉS ensuite
  // (donateur reconnu qui n'a plus accès au contact qu'il a donné).
  const buildOptions = () => {
    const opts = [];
    if (email) {
      opts.push({
        key: "email", channelName: "email", icon: "mail",
        label: "Code par email", detail: email,
      });
    }
    if (phone && smsEnabled) {
      opts.push({
        key: "sms", channelName: "sms", icon: "phone",
        label: "Code par SMS", detail: phone,
      });
    }
    if (phone && whatsappEnabled) {
      opts.push({
        key: "whatsapp", channelName: "whatsapp", icon: "whatsapp",
        label: "Lien par WhatsApp", detail: phone,
      });
    }
    // Canaux enregistrés chez la synagogue — uniquement si le canal n'est pas
    // déjà couvert par un contact tapé. Le contact réel n'est JAMAIS montré,
    // même masqué : rien n'a encore été prouvé à ce stade.
    if (recognized?.has_phone && !phone && smsEnabled && email) {
      opts.push({
        key: "donor-sms", channelName: "sms", icon: "phone",
        useDonorContact: "phone",
        label: "Code par SMS",
        detail: "vers votre numéro connu de la synagogue",
      });
    }
    if (recognized?.has_email && !email && phone) {
      opts.push({
        key: "donor-email", channelName: "email", icon: "mail",
        useDonorContact: "email",
        label: "Code par email",
        detail: "vers votre email connu de la synagogue",
      });
    }
    return opts;
  };

  const handleTriggerClick = () => {
    if (disabled) return;
    const opts = buildOptions();
    if (opts.length === 0) {
      toast.error("Renseignez d'abord votre email ou votre téléphone.");
      return;
    }
    if (opts.length > 1
        || (opts[0].channelName === "sms" && smsBlocked)) {
      // SMS seul mais en attente/épuisé → on montre la carte désactivée avec
      // le chrono plutôt que d'envoyer (le serveur refuserait de toute façon).
      setMode("choosing");
      return;
    }
    sendVerification(opts[0]);
  };

  const sendVerification = async (option) => {
    if (option.channelName === "sms" && smsBlocked) {
      setMode("choosing");
      return;
    }
    setMode("sending");
    setSendingKey(option.key);
    setChannel(option.channelName);
    setCode("");
    codeSentRef.current = false;
    try {
      const body = {
        organization_id: organizationId,
        channel: option.channelName,
        first_name: firstName || undefined,
        context,
      };
      if (option.useDonorContact) {
        // Clé de recherche = le contact tapé ; le serveur retrouve la fiche
        // et envoie vers le contact enregistré correspondant.
        body.use_donor_contact = option.useDonorContact;
        if (email) body.email = email;
        else body.phone = phone;
      } else if (option.channelName === "email") {
        body.email = email;
      } else {
        body.phone = phone;
      }
      const r = await axios.post(`${PUBLIC_API}/public/identity/send`, body);
      setToken(r.data.token);
      setMaskedDestination(r.data.masked_destination || "");
      setCodeMode(r.data.mode === "code");
      if (option.channelName === "sms") {
        setSmsSends((l) => [...l.filter((t) => Date.now() - t < 1800000), Date.now()]);
        setCooldownUntil(Date.now() + 60000);
        setNowTs(Date.now());
      }
      setMode("waiting");
      toast.success(
        option.channelName === "email"
          ? `Code envoyé par email à ${r.data.masked_destination}.`
          : option.channelName === "whatsapp"
            ? `WhatsApp envoyé à ${r.data.masked_destination}.`
            : `Code envoyé par SMS à ${r.data.masked_destination}.`,
      );
      startPolling(r.data.token);
    } catch (e) {
      setMode("idle");
      toast.error(
        e?.response?.data?.detail ||
          "Impossible d'envoyer la vérification. Réessayez.",
      );
    }
  };

  const submitCode = async (value) => {
    const cleaned = (value ?? code).replace(/\D/g, "");
    if (cleaned.length < 6 || codeSubmitting || codeSentRef.current) return;
    setCodeSubmitting(true);
    try {
      const r = await axios.post(
        `${PUBLIC_API}/public/identity/${token}/confirm-code`,
        { code: cleaned },
      );
      if (r.data?.verified) {
        codeSentRef.current = true;
        if (pollRef.current) clearInterval(pollRef.current);
        setMode("verified");
        toast.success("Identité confirmée !");
        if (onVerified) onVerified(token, channel);
      }
    } catch (e) {
      // 6 codes faux → le serveur verrouille (429) : inutile d'insister,
      // on bascule sur « refaites une demande » au lieu d'un champ mort.
      if (e?.response?.status === 429 || e?.response?.status === 410) {
        if (pollRef.current) clearInterval(pollRef.current);
        setLockMsg(e?.response?.data?.detail || "Trop de tentatives. Refaites une demande.");
        setMode("expired");
      } else {
        toast.error(e?.response?.data?.detail || "Code incorrect.");
      }
      setCode("");
    } finally {
      setCodeSubmitting(false);
    }
  };

  const restart = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    setMode("idle");
    setToken("");
    setMaskedDestination("");
    setCode("");
    setCodeMode(false);
    setLockMsg("");
  };

  const OptionIcon = ({ icon, className }) =>
    icon === "whatsapp" ? (
      <WhatsappIcon className={className} />
    ) : icon === "phone" ? (
      <Phone className={className} />
    ) : (
      <Mail className={className} />
    );

  // ── Render ──────────────────────────────────────────────────────────────

  if (mode === "verified") {
    return (
      <div className="rounded-2xl border border-emerald-300 bg-emerald-50 p-4 flex items-center justify-between gap-3">
        <div className="flex items-start gap-2.5">
          <div className="h-8 w-8 rounded-full bg-emerald-500 flex items-center justify-center shrink-0">
            <Check className="h-4 w-4 text-white" />
          </div>
          <div>
            <p className="text-sm font-semibold text-emerald-800">
              Identité vérifiée
            </p>
            <p className="text-xs text-emerald-700 mt-0.5">
              Vous pouvez maintenant valider votre {context === "kiddoush" ? "réservation" : "don"}.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={restart}
          className="text-xs text-emerald-700 underline hover:text-emerald-900 shrink-0"
        >
          Changer de contact
        </button>
      </div>
    );
  }

  if (mode === "waiting" || mode === "expired") {
    const expired = mode === "expired";
    return (
      <div className={`rounded-2xl border p-5 ${expired ? "border-red-200 bg-red-50" : "border-amber-200 bg-amber-50"}`}>
        <div className="flex items-start gap-3 mb-4">
          <div className={`h-10 w-10 rounded-full flex items-center justify-center shrink-0 ${expired ? "bg-red-100" : "bg-amber-100"}`}>
            <OptionIcon
              icon={channel === "whatsapp" ? "whatsapp" : channel === "sms" ? "phone" : "mail"}
              className={`h-5 w-5 ${expired ? "text-red-600" : "text-amber-600"}`}
            />
          </div>
          <div className="flex-1">
            {expired ? (
              <>
                <p className="text-sm font-semibold text-red-800">
                  {lockMsg ? "Vérification bloquée" : codeMode ? "Le code a expiré" : "Le lien a expiré"}
                </p>
                <p className="text-xs text-red-700 mt-1 leading-relaxed">
                  {lockMsg || "Refaites une demande pour vérifier votre identité."}
                </p>
              </>
            ) : codeMode ? (
              <>
                <p className="text-sm font-semibold text-stone-900">
                  Code envoyé par {channel === "email" ? "email" : "SMS"}
                </p>
                <p className="text-xs text-stone-600 mt-1 leading-relaxed">
                  Saisissez le code à 6 chiffres envoyé à <strong>{maskedDestination}</strong>.
                </p>
                <div className="flex items-center gap-2 mt-3">
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    autoFocus
                    maxLength={6}
                    value={code}
                    onChange={(e) => {
                      const v = e.target.value.replace(/\D/g, "").slice(0, 6);
                      setCode(v);
                      if (v.length === 6) submitCode(v);
                    }}
                    placeholder="••••••"
                    data-testid="identity-code-input"
                    className="w-32 px-3 py-2.5 rounded-xl border border-stone-300 bg-white text-center text-lg tracking-[0.35em] font-semibold outline-none focus:border-stone-900"
                  />
                  <button
                    type="button"
                    onClick={() => submitCode()}
                    disabled={code.length < 6 || codeSubmitting}
                    className="px-4 py-2.5 rounded-xl bg-stone-900 text-white text-sm font-medium disabled:opacity-40 flex items-center gap-1.5"
                  >
                    {codeSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                    Valider
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="text-sm font-semibold text-stone-900">
                  {channel === "whatsapp" ? "WhatsApp envoyé" : "Email envoyé"}
                </p>
                <p className="text-xs text-stone-600 mt-1 leading-relaxed">
                  Cliquez sur le lien envoyé à <strong>{maskedDestination}</strong> puis revenez sur cette page —
                  la suite se débloquera automatiquement.
                </p>
                <div className="flex items-center gap-2 mt-2.5 text-[11px] text-stone-500">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  En attente de votre confirmation…
                </div>
              </>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={restart}
          className="w-full text-xs text-stone-600 hover:text-stone-900 underline flex items-center justify-center gap-1.5 py-1"
        >
          <RotateCcw className="h-3 w-3" />
          {expired
            ? "Recommencer"
            : codeMode && cooldownLeft > 0
              ? `Renvoyer un code (possible dans ${cooldownLeft} s) — ou changer de contact`
              : "Renvoyer ou changer de contact"}
        </button>
      </div>
    );
  }

  // Donateur RECONNU : l'écran de choix du canal est TOUJOURS affiché — rien
  // ne part tant qu'il n'a pas touché une carte, même s'il n'y a qu'un canal
  // (il voit ainsi OÙ le lien/code va partir). Sinon, le choix ne s'ouvre
  // qu'après le bouton « Vérifier mon identité » (comportement historique).
  const chooserOpts = orgConfigLoaded ? buildOptions() : [];
  const showChooser =
    mode === "choosing"
    || (Boolean(recognized) && (mode === "idle" || mode === "sending") && chooserOpts.length > 0);
  if (showChooser) {
    const sending = mode === "sending";
    return (
      <div className="rounded-2xl border border-stone-200 bg-white p-5">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-start gap-2.5">
            <ShieldCheck className="h-5 w-5 text-stone-700 mt-0.5" />
            <p className="text-sm font-medium text-stone-900">
              Comment voulez-vous vérifier votre identité&nbsp;?
            </p>
          </div>
          {!recognized && (
            <button
              type="button"
              onClick={() => setMode("idle")}
              className="text-stone-400 hover:text-stone-700"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <div className="space-y-2">
          {chooserOpts.map((o) => {
            const isSms = o.channelName === "sms";
            const smsExhausted = isSms && smsRecentCount >= 3;
            const smsCooling = isSms && !smsExhausted && cooldownLeft > 0;
            const blocked = sending || smsExhausted || smsCooling;
            return (
              <button
                key={o.key}
                type="button"
                onClick={() => sendVerification(o)}
                disabled={blocked}
                data-testid={`identity-option-${o.key}`}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border transition-colors text-left disabled:opacity-60 disabled:cursor-not-allowed ${
                  o.icon === "whatsapp"
                    ? "border-stone-200 hover:border-emerald-500 hover:bg-emerald-50"
                    : "border-stone-200 hover:border-stone-900 hover:bg-stone-50"
                }`}
              >
                <div className={`h-9 w-9 rounded-full flex items-center justify-center shrink-0 ${o.icon === "whatsapp" ? "bg-emerald-100" : "bg-stone-100"}`}>
                  {sending && sendingKey === o.key ? (
                    <Loader2 className="h-4 w-4 animate-spin text-stone-700" />
                  ) : (
                    <OptionIcon icon={o.icon} className={`h-4 w-4 ${o.icon === "whatsapp" ? "text-emerald-600" : "text-stone-700"}`} />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-stone-900">{o.label}</p>
                  <p className="text-xs text-stone-500 truncate">
                    {smsExhausted
                      ? "3 SMS maximum — réessayez plus tard ou passez par email"
                      : smsCooling
                        ? `nouvel envoi possible dans ${cooldownLeft} s`
                        : o.detail}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // mode === "idle" or "sending"
  const idleOpts = orgConfigLoaded ? buildOptions() : [];
  const canTrigger = orgConfigLoaded && idleOpts.length > 0;
  // Contact présent mais AUCUN canal utilisable (ex. : reconnu par téléphone,
  // SMS non activé, pas d'email sur la fiche) → le dire clairement plutôt
  // qu'un bouton qui ne mène nulle part.
  if (orgConfigLoaded && (email || phone) && idleOpts.length === 0) {
    return (
      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
        <div className="flex items-start gap-3">
          <ShieldCheck className="h-5 w-5 text-amber-700 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-semibold text-stone-900">
              Vérification impossible avec ce contact
            </p>
            <p className="text-xs text-stone-600 mt-1 leading-relaxed">
              Aucun moyen de vérification n'est disponible pour ce numéro.
              Utilisez une adresse email pour continuer.
            </p>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="rounded-2xl border border-stone-300 bg-stone-50 p-4">
      <div className="flex items-start gap-3 mb-3">
        <ShieldCheck className="h-5 w-5 text-stone-700 mt-0.5 shrink-0" />
        <div className="flex-1">
          <p className="text-sm font-semibold text-stone-900">
            Vérifiez votre identité
          </p>
          <p className="text-xs text-stone-600 mt-1 leading-relaxed">
            Pour des raisons de sécurité, nous devons vérifier que c'est bien vous.
            {smsEnabled
              ? " Vous recevrez un code par email ou par SMS, au choix, à saisir ici."
              : whatsappEnabled
                ? " Vous recevrez un code par email (à saisir ici) ou un lien par WhatsApp, au choix."
                : " Vous recevrez un code par email, à saisir ici."}
          </p>
        </div>
      </div>
      <button
        type="button"
        onClick={handleTriggerClick}
        disabled={!canTrigger || disabled || mode === "sending"}
        className="w-full py-3 rounded-xl bg-stone-900 text-white text-sm font-medium flex items-center justify-center gap-2 hover:bg-stone-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {mode === "sending" ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Send className="h-4 w-4" />
        )}
        Vérifier mon identité
      </button>
      {!canTrigger && orgConfigLoaded && (
        <p className="text-[11px] text-stone-500 text-center mt-2">
          Renseignez votre email{smsEnabled ? " ou téléphone" : ""} pour activer la vérification.
        </p>
      )}
    </div>
  );
}
